import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import { DrawingOptions, DrawingSettingGroup, DrawingTypes, LabelAxisViewConfig, SettingValues } from '../drawing.interface'
import { ChartEvents } from '../../chart.events'
import { Coordinate, MouseEventParams, PrimitiveHoveredItem } from 'lightweight-charts'
import { determineChartX, determineChartY, scaledCoordinate } from '../drawing.helper'
import { AlertExpression_Type, UserAlert, UserAlert_Status } from '../../../../proto/generated/nexus'
import { alpha } from '@mui/material'
import {
    formatSecurityLabelForAlert,
    getPrimaryChartAlertThresholdPrice,
    getUserAlertMessageFromCondition,
    getUserAlertUnderlyingSecurity,
    hasUsableTopLevelAlertSecurity,
    isDirectSecurityPriceAlert,
    isDrawingAlert,
    getDrawingFromAlert,
    alertTimestampMsToChartTime,
    isChannelAlertDrawingType,
    isUserAlertVolumeLevelThreshold,
    getChartAlertLineTagParts,
    getChartAlertStatusColorLabel,
    CHART_ALERT_STATUS_SEPARATOR,
    measureChartAlertLineTagWidth,
    resolveColorForAlertStatus,
    truncateChartAlertLineTagParts,
} from '../../../../util/alerts'
import { isZoneOperator } from '../../../alerts/alertOperatorDropdown/alertOperatorDropdown'
import {
    getFibonacciAlertLevelFromDrawing,
    getFibonacciChannelBoundLevelsFromOptions,
    getFibonacciLevelPrice,
    parseFibonacciAlertLevel,
} from '../../../../util/fibonacci'
import { getDistanceFromSegment } from '../drawing.helper'
import { getEffectiveAlertSettings } from '../../../../util/chart'
import { darkHoverBtnColor, lightHoverBtnColor } from '../../../../theme/theme'
import { Security } from '../../../../proto/generated/security'

export type ChartAlertsOnChartDrawingType = 'chartAlertsOnChart'

export type ChartAlertsOnChartState = {
    alerts: UserAlert[]
}

export type ChartAlertsOnChartOptions = {
    drawingType: ChartAlertsOnChartDrawingType
}

export type ChartAlertsOnChartSettings = {
    alerts: UserAlert[]
}

type HitRegion = {
    alertId: string
    trash: { x1: number; y1: number; x2: number; y2: number } | null
    restart: { x1: number; y1: number; x2: number; y2: number } | null
    pill: { x1: number; y1: number; x2: number; y2: number } | null
    lineY1: number
    lineY2: number
    handle: { cx: number; cy: number; r: number } | null
    tick: { cx: number; cy: number; r: number } | null
    tickBox?: { x1: number; y1: number; x2: number; y2: number } | null
    pricePill: { x1: number; y1: number; x2: number; y2: number } | null
    editable: boolean
    trendlineSegment?: { x1: number; y1: number; x2: number; y2: number }
    rectangleRegion?: { x1: number; y1: number; x2: number; y2: number }
    channelSegments?: Array<{ x1: number; y1: number; x2: number; y2: number }>
}

type LayoutEntry = {
    alert: UserAlert
    price: number
    y: number
    expanded: boolean
}

const priceMultiplier = 100

/** Half-height of the draggable strip around the price line: this many logical px × verticalPixelRatio. */
const ALERT_LINE_HIT_BAND_LOGICAL = 5

/** Pane-local px; ignore sub-threshold jitter so a tiny press/move is not treated as a drag (same idea as OrdersOnChartTool). */
const ALERT_LINE_DRAG_THRESHOLD_PX = 3

type HandleScrollSnapshot = {
    mouseWheel: boolean
    pressedMouseMove: boolean
    pressedHorizontalMouseMove: boolean
    pressedVerticalMouseMove: boolean
    horzTouchDrag: boolean
    vertTouchDrag: boolean
}

const defaultHandleScrollSnapshot = (): HandleScrollSnapshot => ({
    mouseWheel: true,
    pressedMouseMove: true,
    pressedHorizontalMouseMove: true,
    pressedVerticalMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: true
})

class ChartAlertsOnChartTool extends DrawingBase<
    ChartAlertsOnChartOptions,
    ChartAlertsOnChartSettings,
    ChartAlertsOnChartState
> {
    initialPoint = 0
    fixedDrawing = true

    private hitRegions: HitRegion[] = []
    private draggingAlertId: string | null = null
    /** Pane-local Y when line drag started; used with {@link ALERT_LINE_DRAG_THRESHOLD_PX}. */
    private alertLineDragStartPaneY: number | null = null
    private alertLineDragThresholdPassed = false
    private dragPreviewPrice: number | null = null
    private hoveredAlertId: string | null = null
    private hoveredTrashAlertId: string | null = null
    private hoveredRestartAlertId: string | null = null
    private selectedAlertId: string | null = null
    private lastDragEndedAt = 0
    /** Full handleScroll before alert drag; lightweight-charts also uses pressed* split flags. */
    private handleScrollSnapshotBeforeAlertDrag: HandleScrollSnapshot | null = null
    /** Last draw layout for hit-testing (bitmap coords). */
    private lastLayout: { plotRight: number; entries: LayoutEntry[]; centerX: number; canvasW: number } | null = null

    private expandedPillTextByAlertId: Record<string, string> = {}
    private expandedPillFetchGeneration = 0
    /** Uncommitted threshold edits on overlay charts; persisted only after tick confirm. */
    private pendingOverlayPriceByAlertId: Record<string, number> = {}
    /** Client-only draft alert rendered on-chart until user confirms/cancels. */
    private temporaryAlert: UserAlert | null = null
    /** True once this temp id appeared in {@link ChartAlertsOnChartSettings.alerts}; used to detect removal after deactivate. */
    private temporaryAlertSyncedIntoChartSettings = false
    /** Inline numeric editor shown when tapping/clicking overlay pencil price pill. */
    private priceEditInputEl: HTMLInputElement | null = null
    private priceEditInputAlertId: string | null = null

    /** Tap-out / tick-to-save UX: overlay price-scale charts and embedded touch (WebView) charts. */
    private deferAlertEdits(): boolean {
        return this._chart.usesOverlayPriceScale()
    }

    private paneTouchStart: { cx: number; cy: number; t: number } | null = null
    private lastTouchTapHandledAt = 0
    /** After a line drag ends, ignore the synthetic lift tap (pane touchend vs document order). */
    private suppressPaneTapUntil = 0
    private pendingTickCommitAlertId: string | null = null

    private onDocMouseMove = (e: MouseEvent) => this.handleDocPointerMove(e)
    private onDocMouseUp = (e: MouseEvent) => this.handleDocPointerUp(e)
    private onDocTouchMove = (e: TouchEvent) => this.handleDocTouchMove(e)
    private onDocTouchEnd = (e: TouchEvent) => this.handleDocTouchEnd(e)
    private onPaneTouchMove = (e: TouchEvent) => this.handleDocTouchMove(e)

    constructor(options: DrawingOptions<any, any, any>) {
        super(options)
        if (this._chartApi) {
            this._chartApi.subscribeClick(this.handleChartClick)
            this._chartApi.subscribeDblClick(this.handleChartDoubleClick)
        }
        const paneElement = this._chart.getPaneElementById(this._paneId)
        if (paneElement) {
            if (this._chart.touchEnabled) {
                paneElement.addEventListener('touchstart', this.onPanePointerDown, { passive: false })
                paneElement.addEventListener('touchend', this.onPanePointerUp, { passive: true })
                paneElement.addEventListener('touchmove', this.onPaneTouchMove, { passive: false })
            } else {
                paneElement.addEventListener('mousedown', this.onPanePointerDown)
                paneElement.addEventListener('mousemove', this.onPaneMouseMove)
                paneElement.addEventListener('mouseleave', this.onPaneMouseLeave)
            }
        }
    }

    private isClientChartTemporaryAlertId(id: string | undefined): boolean {
        return Boolean(id?.startsWith('tmp-chart-alert-'))
    }

    private isOnTick(r: HitRegion, sx: number, sy: number, extra = 6): boolean {
        if (r.tickBox) {
            const padX = extra * this.horizontalPixelRatio
            const padY = extra * this.verticalPixelRatio
            return (
                sx >= r.tickBox.x1 - padX &&
                sx <= r.tickBox.x2 + padX &&
                sy >= r.tickBox.y1 - padY &&
                sy <= r.tickBox.y2 + padY
            )
        }
        if (!r.tick) return false
        return Math.hypot(sx - r.tick.cx, sy - r.tick.cy) <= r.tick.r + extra * this.horizontalPixelRatio
    }

    private commitOverlayTick(alertId: string): void {
        const alert = this.getAlertById(alertId)
        if (!alert) return
        const isTemporary =
            this.temporaryAlert?.id === alert.id || this.isClientChartTemporaryAlertId(alert.id)
        if (!isTemporary && !isDirectSecurityPriceAlert(alert)) return
        const server = getPrimaryChartAlertThresholdPrice(alert)
        const display = this.getDisplayPrice(alert)
        if (isTemporary) {
            this._chart.publishEvent(ChartEvents.CHART_ALERT_ON_CHART_CREATE_COMMIT, {
                alert,
                newPrice: display
            })
            void window.alertsManager?.createChartTemporaryAlert?.(alert, display)
            this.lastDragEndedAt = Date.now()
            this.updateDrawing()
            return
        }
        if (
            server !== undefined &&
            display !== undefined &&
            Math.abs(display - server) > 1e-8
        ) {
            this._chart.publishEvent(ChartEvents.CHART_ALERT_ON_CHART_PRICE_COMMIT, {
                alert,
                newPrice: display
            })
            this.lastDragEndedAt = Date.now()
        }
        this.assignSelectedAlert(null)
        this.updateDrawing()
    }

    private isOnActionChip(r: HitRegion, sx: number, sy: number): boolean {
        if (this.isOnTick(r, sx, sy)) return true
        if (
            r.restart &&
            sx >= r.restart.x1 &&
            sx <= r.restart.x2 &&
            sy >= r.restart.y1 &&
            sy <= r.restart.y2
        ) {
            return true
        }
        if (
            r.trash &&
            sx >= r.trash.x1 &&
            sx <= r.trash.x2 &&
            sy >= r.trash.y1 &&
            sy <= r.trash.y2
        ) {
            return true
        }
        return false
    }

    private getRenderableAlerts(): UserAlert[] {
        if (!this.temporaryAlert) return this._state.alerts ?? []
        const base = this._state.alerts?.filter((a) => a.id !== this.temporaryAlert!.id) ?? []
        const temp: UserAlert = {
            ...this.temporaryAlert,
            status: UserAlert_Status.ACTIVE
        }
        return [...base, temp]
    }

    private getAlertById(alertId: string): UserAlert | undefined {
        if (this.temporaryAlert?.id === alertId) {
            return { ...this.temporaryAlert, status: UserAlert_Status.ACTIVE }
        }
        return this._state.alerts.find((a) => a.id === alertId)
    }

    private clearTemporaryAlertInternal() {
        if (!this.temporaryAlert) return
        const tempId = this.temporaryAlert.id
        this.temporaryAlert = null
        this.temporaryAlertSyncedIntoChartSettings = false
        delete this.pendingOverlayPriceByAlertId[tempId]
        if (this.selectedAlertId === tempId) this.selectedAlertId = null
        if (this.hoveredAlertId === tempId) this.hoveredAlertId = null
        if (this.hoveredTrashAlertId === tempId) this.hoveredTrashAlertId = null
        if (this.hoveredRestartAlertId === tempId) this.hoveredRestartAlertId = null
        this._chart.clearManagerStagedChartAlertPartitionKey()
    }

    /** Stage a temporary on-chart alert; confirm with tick, cancel by tapping outside. */
    setTemporaryAlertForDrag = (alert: UserAlert) => {
        const id = alert.id?.trim() ? alert.id : `tmp-chart-alert-${Date.now()}`
        this.clearTemporaryAlertInternal()
        this.temporaryAlertSyncedIntoChartSettings = false
        this.temporaryAlert = {
            ...alert,
            id,
            status: UserAlert_Status.ACTIVE
        }
        const initialPrice = getPrimaryChartAlertThresholdPrice(this.temporaryAlert)
        if (initialPrice != null) {
            this.pendingOverlayPriceByAlertId[id] = initialPrice
        }
        this.selectedAlertId = id
        this.hoveredAlertId = null
        this.updateDrawing()
    }

    clearTemporaryAlertForDrag = () => {
        this.clearTemporaryAlertInternal()
        this.assignSelectedAlert(null)
        this.updateDrawing()
    }

    private isUiExpanded(alertId: string): boolean {
        return (
            this.draggingAlertId === alertId ||
            this.hoveredAlertId === alertId ||
            this.selectedAlertId === alertId
        )
    }

    private roundDraggedThreshold(price: number): number {
        const alert = this.draggingAlertId ? this.getAlertById(this.draggingAlertId) : undefined
        const lhs = alert?.condition?.securityAlertCondition?.lhsExpression
        if (lhs?.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
            return Math.round(price * 100) / 100
        }
        if (alert && isUserAlertVolumeLevelThreshold(alert)) {
            return Math.max(0, Math.round(price))
        }
        const tickSize = this._chart?.securities?.[0]?.tickSize || 5
        return (Math.round(Math.round(price * priceMultiplier) / tickSize) * tickSize) / priceMultiplier
    }

    private normalizeManualThreshold(alert: UserAlert, price: number): number {
        const lhs = alert?.condition?.securityAlertCondition?.lhsExpression
        if (lhs?.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
            return Math.round(price * 100) / 100
        }
        if (isUserAlertVolumeLevelThreshold(alert)) {
            return Math.max(0, Math.round(price))
        }
        const tickSize = this._chart?.securities?.[0]?.tickSize || 5
        return (Math.round(Math.round(price * priceMultiplier) / tickSize) * tickSize) / priceMultiplier
    }

    private removeOverlayInlinePriceEditor() {
        if (!this.priceEditInputEl) return
        this.priceEditInputEl.remove()
        this.priceEditInputEl = null
        this.priceEditInputAlertId = null
    }

    private submitOverlayInlinePriceEdit() {
        const alertId = this.priceEditInputAlertId
        const input = this.priceEditInputEl
        if (!alertId || !input) {
            this.removeOverlayInlinePriceEditor()
            return
        }
        const alert = this.getAlertById(alertId)
        const raw = input.value?.trim()
        this.removeOverlayInlinePriceEditor()
        if (!alert || !raw) return
        const numeric = Number(raw)
        if (!Number.isFinite(numeric)) return
        const nextPrice = this.normalizeManualThreshold(alert, numeric)
        this.pendingOverlayPriceByAlertId[alert.id] = nextPrice
        this.assignSelectedAlert(alert.id)
        this.updateDrawing()
    }

    /** Overlay price-pill pencil edit: inline numeric textfield anchored on the price pill. */
    private editOverlayPriceAsNumber(alertId: string, pricePill: { x1: number; y1: number; x2: number; y2: number }): boolean {
        if (!this.deferAlertEdits()) return false
        const alert = this.getAlertById(alertId)
        if (!alert || !this.isAlertActive(alert) || !isDirectSecurityPriceAlert(alert)) return false
        const currentPrice = this.getDisplayPrice(alert)
        if (currentPrice === undefined) return false
        const paneElement = this._chart.getPaneElementById(this._paneId)
        if (!paneElement) return false
        this.removeOverlayInlinePriceEditor()
        const input = document.createElement('input')
        input.type = 'number'
        input.step = 'any'
        input.value = String(currentPrice)
        input.setAttribute('inputmode', 'decimal')
        const left = Math.max(0, pricePill.x1 / this.horizontalPixelRatio)
        const top = Math.max(0, pricePill.y1 / this.verticalPixelRatio)
        const width = Math.max(48, (pricePill.x2 - pricePill.x1) / this.horizontalPixelRatio)
        const height = Math.max(24, (pricePill.y2 - pricePill.y1) / this.verticalPixelRatio)
        input.style.position = 'absolute'
        input.style.left = `${left}px`
        input.style.top = `${top}px`
        input.style.width = `${width}px`
        input.style.height = `${height}px`
        input.style.boxSizing = 'border-box'
        input.style.border = '1px solid rgba(255,255,255,0.28)'
        input.style.borderRadius = '4px'
        input.style.background = 'rgba(10,10,10,0.94)'
        input.style.color = '#fff'
        input.style.padding = '0 8px'
        input.style.font = '12px sans-serif'
        input.style.zIndex = '40'
        input.style.outline = 'none'
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                this.submitOverlayInlinePriceEdit()
                return
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                this.removeOverlayInlinePriceEditor()
            }
        })
        input.addEventListener('blur', () => this.submitOverlayInlinePriceEdit())
        paneElement.appendChild(input)
        this.priceEditInputEl = input
        this.priceEditInputAlertId = alert.id
        input.focus()
        input.select()
        this.assignSelectedAlert(alert.id)
        this.updateDrawing()
        return true
    }

    private getDisplayPrice(alert: UserAlert): number | undefined {
        const base = getPrimaryChartAlertThresholdPrice(alert)
        if (base === undefined) return undefined
        if (this.draggingAlertId === alert.id && this.dragPreviewPrice != null) {
            return this.dragPreviewPrice
        }
        if (this.deferAlertEdits()) {
            const pending = this.pendingOverlayPriceByAlertId[alert.id]
            if (pending != null) return pending
        }
        return base
    }

    private assignSelectedAlert(id: string | null) {
        const prev = this.selectedAlertId
        if (!this.deferAlertEdits()) {
            if (id === null) {
                this.clearTemporaryAlertInternal()
            }
            this.selectedAlertId = id
            return
        }
        if (id === null) {
            this.clearTemporaryAlertInternal()
            this.pendingOverlayPriceByAlertId = {}
            this.hoveredAlertId = null
        } else if (prev !== id) {
            for (const k of Object.keys(this.pendingOverlayPriceByAlertId)) {
                if (k !== id) delete this.pendingOverlayPriceByAlertId[k]
            }
        }
        this.selectedAlertId = id
    }

    private isAlertActive(alert: UserAlert): boolean {
        return alert.status === UserAlert_Status.ACTIVE
    }

    private showRestartAction(alert: UserAlert): boolean {
        return alert.status === UserAlert_Status.STOPPED
    }

    private getAlertLineColors() {
        const { palette } = this._chart.theme
        const { alertLineColor, alertLineColorOpacity } = getEffectiveAlertSettings(this._chart.chartConfig)
        const lineActive = alpha(alertLineColor, alertLineColorOpacity)
        const lineInactive = alpha(alertLineColor, alertLineColorOpacity * 0.54)
        const lineActiveEmphasis = alpha(alertLineColor, Math.min(1, alertLineColorOpacity + 0.06))
        const lineInactiveEmphasis = alpha(alertLineColor, alertLineColorOpacity * 0.85)
        return {
            lineActive,
            lineInactive,
            lineActiveEmphasis,
            lineInactiveEmphasis,
        }
    }

    private attachDocDragListeners() {
        if (this._chart.touchEnabled) {
            document.addEventListener('touchend', this.onDocTouchEnd, { passive: true })
        } else {
            document.addEventListener('mouseup', this.onDocMouseUp)
        }
    }

    private detachDocDragListeners() {
        document.removeEventListener('mouseup', this.onDocMouseUp)
        document.removeEventListener('touchend', this.onDocTouchEnd)
    }

    private captureHandleScrollSnapshot(): HandleScrollSnapshot {
        const hs = this._chartApi?.options()?.handleScroll
        if (hs && typeof hs === 'object') {
            const o = hs as unknown as Record<string, boolean | undefined>
            return {
                mouseWheel: Boolean(o.mouseWheel),
                pressedMouseMove: Boolean(o.pressedMouseMove),
                pressedHorizontalMouseMove: Boolean(o.pressedHorizontalMouseMove),
                pressedVerticalMouseMove: Boolean(o.pressedVerticalMouseMove),
                horzTouchDrag: Boolean(o.horzTouchDrag),
                vertTouchDrag: Boolean(o.vertTouchDrag)
            }
        }
        return defaultHandleScrollSnapshot()
    }

    private setDragScrollLock(locked: boolean) {
        if (locked) {
            if (this.handleScrollSnapshotBeforeAlertDrag === null) {
                this.handleScrollSnapshotBeforeAlertDrag = this.captureHandleScrollSnapshot()
            }
            const snap = this.handleScrollSnapshotBeforeAlertDrag
            this._chart.updateLayoutOptions({
                handleScroll: {
                    ...snap,
                    pressedMouseMove: false,
                    pressedHorizontalMouseMove: false,
                    pressedVerticalMouseMove: false
                }
            })
            return
        }

        const restore = this.handleScrollSnapshotBeforeAlertDrag
        this.handleScrollSnapshotBeforeAlertDrag = null
        if (restore) {
            this._chart.updateLayoutOptions({
                handleScroll: { ...restore }
            })
        }
    }

    private onPaneMouseMove = (event: MouseEvent) => {
        if (this.draggingAlertId) {
            if (!this._chartBounds) return
            const chartY = this.getClampedDragChartY(event.clientY)
            if (!this.hasAlertLineDragPassedThreshold(chartY)) return
            event.preventDefault()
            event.stopPropagation()
            this.handleDocPointerMove(event)
            return
        }
        this._chartBounds = this.getPaneElement().getBoundingClientRect()
        const chartX = this.determineAlertChartX(event.clientX)
        const chartY = this.getPaneChartYForHitTesting(event.clientY)
        if (chartX == null || chartY == null) {
            this.setHoverState(null, null, null)
            return
        }
        const sx = scaledCoordinate(chartX, this.horizontalPixelRatio)
        const sy = scaledCoordinate(chartY, this.verticalPixelRatio)
        const next = this.pickHoveredAlertId(sx, sy, chartX)
        const trashId = this.pickHoveredTrashAlertId(sx, sy)
        const restartId = this.pickHoveredRestartAlertId(sx, sy)
        this.setHoverState(next, trashId, restartId)
    }

    private onPaneMouseLeave = () => {
        if (!this.draggingAlertId) {
            this.setHoverState(null, null, null)
        }
    }

    private pickHoveredRestartAlertId(sx: number, sy: number): string | null {
        for (const r of this.hitRegions) {
            if (
                r.restart &&
                sx >= r.restart.x1 &&
                sx <= r.restart.x2 &&
                sy >= r.restart.y1 &&
                sy <= r.restart.y2
            ) {
                return r.alertId
            }
        }
        return null
    }

    private pickHoveredTrashAlertId(sx: number, sy: number): string | null {
        for (const r of this.hitRegions) {
            if (
                r.trash &&
                sx >= r.trash.x1 &&
                sx <= r.trash.x2 &&
                sy >= r.trash.y1 &&
                sy <= r.trash.y2
            ) {
                return r.alertId
            }
        }
        return null
    }

    private setHoverState(alertId: string | null, trashAlertId: string | null, restartAlertId: string | null) {
        if (
            this.hoveredAlertId === alertId &&
            this.hoveredTrashAlertId === trashAlertId &&
            this.hoveredRestartAlertId === restartAlertId
        ) {
            return
        }
        this.hoveredAlertId = alertId
        this.hoveredTrashAlertId = trashAlertId
        this.hoveredRestartAlertId = restartAlertId
        this.updateDrawing()
    }

    private setHoveredAlert(id: string | null) {
        this.setHoverState(id, this.hoveredTrashAlertId, this.hoveredRestartAlertId)
    }

    private getActionButtonStyle(
        isHovered: boolean,
        palette: { mode: 'light' | 'dark'; text: { primary: string }; divider: string },
        defaultBg: string,
        iconColor: string
    ) {
        const hoverBg = alpha(
            palette.mode === 'light' ? lightHoverBtnColor : darkHoverBtnColor,
            0.8
        )
        return {
            bg: isHovered ? hoverBg : defaultBg,
            border: alpha(palette.divider, isHovered ? 0.65 : 0.4),
            icon: iconColor,
        }
    }

    /**
     * When overlay / embedded layout hides price scales, `priceScale('left').width()` can disagree with the
     * pane DOM — `determineChartX` returns null and line drag never starts. Fall back to pane-relative X in
     * that mode when the pointer is clearly over the plot strip.
     */
    private determineAlertChartX(clientX: number): number | null {
        if (!this._chartBounds) return null
        const x = determineChartX(this._chartApi, this._chartBounds, clientX)
        if (x != null) return x
        if (!this.deferAlertEdits()) return null
        const raw = clientX - this._chartBounds.left
        const plotW = this._chartApi.timeScale().width()
        if (plotW <= 0) return null
        const clamped = Math.max(0, Math.min(plotW, raw))
        return clamped
    }

    private refreshExpandedPillTexts = async (): Promise<void> => {
        const gen = ++this.expandedPillFetchGeneration
        const alerts = this.getRenderableAlerts()
        const next: Record<string, string> = {}
        for (const alert of alerts) {
            try {
                const sac = alert.condition?.securityAlertCondition
                const lhs = sac?.lhsExpression
                let sym = ''
                if (lhs?.type === AlertExpression_Type.SIMPLE_SECURITY && lhs.simpleSecurity?.security) {
                    sym = await formatSecurityLabelForAlert(lhs.simpleSecurity.security)
                } else if (hasUsableTopLevelAlertSecurity(alert.security)) {
                    sym = await formatSecurityLabelForAlert(alert.security as Security)
                } else {
                    const [secs] = getUserAlertUnderlyingSecurity(alert)
                    if (secs[0]) sym = await formatSecurityLabelForAlert(secs[0])
                }
                const msg =
                    (await getUserAlertMessageFromCondition(alert.condition, sym, [])).trim() ||
                    alert.message ||
                    alert.name ||
                    'Alert'
                next[alert.id] = msg
            } catch {
                next[alert.id] = alert.message || alert.name || 'Alert'
            }
        }
        if (gen !== this.expandedPillFetchGeneration) return
        this.expandedPillTextByAlertId = next
        this.updateDrawing()
    }

    /** Prefer alert whose line is closest when multiple bands overlap. */
    private pickHoveredAlertId(sx: number, sy: number, chartXMedia: number): string | null {
        const layout = this.lastLayout
        if (!layout) return null
        for (const r of this.hitRegions) {
            if (r.pill && sx >= r.pill.x1 && sx <= r.pill.x2 && sy >= r.pill.y1 && sy <= r.pill.y2) {
                return r.alertId
            }
            if (r.restart && sx >= r.restart.x1 && sx <= r.restart.x2 && sy >= r.restart.y1 && sy <= r.restart.y2) {
                return r.alertId
            }
            if (r.trash && sx >= r.trash.x1 && sx <= r.trash.x2 && sy >= r.trash.y1 && sy <= r.trash.y2) {
                return r.alertId
            }
            if (this.isOnTick(r, sx, sy, 4)) {
                return r.alertId
            }
            if (r.handle) {
                const hr = r.handle.r + 4 * this.horizontalPixelRatio
                if (Math.hypot(sx - r.handle.cx, sy - r.handle.cy) <= hr) {
                    return r.alertId
                }
            }
        }
        if (chartXMedia > this.getTimeScaleWidth()) return null
        const lineBand = ALERT_LINE_HIT_BAND_LOGICAL * this.verticalPixelRatio
        let best: { id: string; d: number } | null = null
        for (const e of layout.entries) {
            const d = Math.abs(sy - e.y)
            if (d <= lineBand && sx >= 0 && sx <= layout.plotRight) {
                if (!best || d < best.d) {
                    best = { id: e.alert.id, d }
                }
            }
        }
        return best ? best.id : null
    }

    private handleChartClick = (param: MouseEventParams): void => {
        const { point } = param
        if (!point) return
        if (Date.now() - this.lastTouchTapHandledAt < 450) return
        this.applyChartTapAtMediaCoords(point.x as number, point.y as number)
    }

    /** Media-coordinate tap (same space as lightweight-charts `point` / pane hit-testing). */
    private applyChartTapAtMediaCoords(chartX: number, chartY: number): void {
        const sx = scaledCoordinate(chartX, this.horizontalPixelRatio)
        const sy = scaledCoordinate(chartY, this.verticalPixelRatio)

        if (this.deferAlertEdits()) {
            const pendingTickId = this.pendingTickCommitAlertId
            this.pendingTickCommitAlertId = null
            for (const r of this.hitRegions) {
                if (!this.isOnTick(r, sx, sy, 10) && r.alertId !== pendingTickId) continue
                this.commitOverlayTick(r.alertId)
                return
            }
            if (pendingTickId) {
                this.commitOverlayTick(pendingTickId)
                return
            }
        }

        for (const r of this.hitRegions) {
            if (
                r.restart &&
                sx >= r.restart.x1 &&
                sx <= r.restart.x2 &&
                sy >= r.restart.y1 &&
                sy <= r.restart.y2
            ) {
                const alert = this.getAlertById(r.alertId)
                if (!alert || !this.showRestartAction(alert)) {
                    return
                }
                if (this._chart.touchEnabled && this.selectedAlertId !== r.alertId) {
                    this.assignSelectedAlert(r.alertId)
                    this.updateDrawing()
                    return
                }
                this._chart.publishEvent(ChartEvents.CHART_ALERT_ON_CHART_RESTART, { alert })
                if (this.selectedAlertId === r.alertId) this.assignSelectedAlert(null)
                return
            }
            if (
                r.trash &&
                sx >= r.trash.x1 &&
                sx <= r.trash.x2 &&
                sy >= r.trash.y1 &&
                sy <= r.trash.y2
            ) {
                const alert = this.getAlertById(r.alertId)
                if (!alert || !this.isAlertActive(alert)) {
                    return
                }
                if (this._chart.touchEnabled && this.selectedAlertId !== r.alertId) {
                    this.assignSelectedAlert(r.alertId)
                    this.updateDrawing()
                    return
                }
                this._chart.publishEvent(ChartEvents.CHART_ALERT_ON_CHART_DELETE, { alert })
                if (this.selectedAlertId === r.alertId) this.assignSelectedAlert(null)
                return
            }
        }

        if (Date.now() - this.lastDragEndedAt < 350 && !this.deferAlertEdits()) {
            return
        }

        const layout = this.lastLayout
        if (!layout) {
            this.assignSelectedAlert(null)
            this.updateDrawing()
            return
        }
        const tsW = this.getTimeScaleWidth()
        for (const e of layout.entries) {
            const lineBand = ALERT_LINE_HIT_BAND_LOGICAL * this.verticalPixelRatio
            if (
                chartX <= tsW &&
                Math.abs(sy - e.y) <= lineBand &&
                sx >= 0 &&
                sx <= layout.plotRight
            ) {
                this.assignSelectedAlert(e.alert.id)
                this.updateDrawing()
                return
            }
        }
        for (const r of this.hitRegions) {
            if (
                r.pill &&
                sx >= r.pill.x1 &&
                sx <= r.pill.x2 &&
                sy >= r.pill.y1 &&
                sy <= r.pill.y2
            ) {
                this.assignSelectedAlert(r.alertId)
                this.updateDrawing()
                return
            }
            if (
                r.pricePill &&
                sx >= r.pricePill.x1 &&
                sx <= r.pricePill.x2 &&
                sy >= r.pricePill.y1 &&
                sy <= r.pricePill.y2
            ) {
                if (this.editOverlayPriceAsNumber(r.alertId, r.pricePill)) return
                this.assignSelectedAlert(r.alertId)
                this.updateDrawing()
                return
            }
            if (
                r.handle &&
                Math.hypot(sx - r.handle.cx, sy - r.handle.cy) <= r.handle.r + 6 * this.horizontalPixelRatio
            ) {
                this.assignSelectedAlert(r.alertId)
                this.updateDrawing()
                return
            }
        }

        this.assignSelectedAlert(null)
        this.updateDrawing()
    }

    private onPanePointerUp = (event: TouchEvent) => {
        if (!this.deferAlertEdits()) {
            this.paneTouchStart = null
            return
        }
        if (this.draggingAlertId) return
        if (this.pendingTickCommitAlertId) {
            const tickId = this.pendingTickCommitAlertId
            this.pendingTickCommitAlertId = null
            this.paneTouchStart = null
            this.lastTouchTapHandledAt = Date.now()
            this.commitOverlayTick(tickId)
            return
        }
        const start = this.paneTouchStart
        this.paneTouchStart = null
        if (!start) return
        const t = event.changedTouches[0]
        if (!t) return
        if (Date.now() < this.suppressPaneTapUntil) return
        if (Math.hypot(t.clientX - start.cx, t.clientY - start.cy) > 18) return
        if (Date.now() - start.t > 900) return
        if (Date.now() - this.lastDragEndedAt < 200) return

        this._chartBounds = this.getPaneElement().getBoundingClientRect()
        const cx = this.determineAlertChartX(t.clientX)
        const cy = this.getPaneChartYForHitTesting(t.clientY)
        if (cx == null || cy == null) return

        this.lastTouchTapHandledAt = Date.now()
        this.applyChartTapAtMediaCoords(cx, cy)
    }

    private handleChartDoubleClick = (param: MouseEventParams): void => {
        const point = param.point
        if (!point) return
        if (Date.now() - this.lastDragEndedAt < 350) return
        const sx = scaledCoordinate(point.x, this.horizontalPixelRatio)
        const sy = scaledCoordinate(point.y, this.verticalPixelRatio)
        const tsW = this.getTimeScaleWidth()
        let targetId: string | null = null

        for (const r of this.hitRegions) {
            if (r.pill && sx >= r.pill.x1 && sx <= r.pill.x2 && sy >= r.pill.y1 && sy <= r.pill.y2) {
                targetId = r.alertId
                break
            }
            if (
                r.pricePill &&
                sx >= r.pricePill.x1 &&
                sx <= r.pricePill.x2 &&
                sy >= r.pricePill.y1 &&
                sy <= r.pricePill.y2
            ) {
                targetId = r.alertId
                break
            }
            if (r.handle && Math.hypot(sx - r.handle.cx, sy - r.handle.cy) <= r.handle.r + 6 * this.horizontalPixelRatio) {
                targetId = r.alertId
                break
            }
        }

        if (!targetId && point.x <= tsW) {
            const lineBand = ALERT_LINE_HIT_BAND_LOGICAL * this.verticalPixelRatio
            const plotRight = scaledCoordinate(tsW, this.horizontalPixelRatio)
            let best: { id: string; d: number } | null = null
            for (const e of this.lastLayout?.entries ?? []) {
                const d = Math.abs(sy - e.y)
                if (d <= lineBand && sx >= 0 && sx <= plotRight) {
                    if (!best || d < best.d) best = { id: e.alert.id, d }
                }
            }
            targetId = best?.id ?? null
        }

        if (!targetId) return
        const alert = this.getAlertById(targetId)
        if (!alert) return
        this._chart.publishEvent(ChartEvents.CHART_ALERT_ON_CHART_EDIT, { alert })
    }

    private onPanePointerDown = (event: MouseEvent | TouchEvent) => {
        if (this._chart.touchEnabled && event.type === 'touchstart') {
            const te = event as TouchEvent
            const t0 = te.touches[0]
            if (t0) {
                this.paneTouchStart = { cx: t0.clientX, cy: t0.clientY, t: Date.now() }
            }
        }
        if (this.draggingAlertId) return
        this.pendingTickCommitAlertId = null
        this._chartBounds = this.getPaneElement().getBoundingClientRect()
        const touch = (event as TouchEvent)?.touches?.[0]
        const clientX = this._chart.touchEnabled ? (touch?.clientX ?? 0) : (event as MouseEvent).clientX
        const clientY = this._chart.touchEnabled ? (touch?.clientY ?? 0) : (event as MouseEvent).clientY
        const chartX = this.determineAlertChartX(clientX)
        const chartY = this.getPaneChartYForHitTesting(clientY)
        if (chartX == null || chartY == null) {
            return
        }
        const sx = scaledCoordinate(chartX, this.horizontalPixelRatio)
        const sy = scaledCoordinate(chartY, this.verticalPixelRatio)
        const plotRightBmp = scaledCoordinate(this.getTimeScaleWidth(), this.horizontalPixelRatio)

        for (const r of this.hitRegions) {
            if (
                r.restart &&
                sx >= r.restart.x1 &&
                sx <= r.restart.x2 &&
                sy >= r.restart.y1 &&
                sy <= r.restart.y2
            ) {
                return
            }
            if (
                r.trash &&
                sx >= r.trash.x1 &&
                sx <= r.trash.x2 &&
                sy >= r.trash.y1 &&
                sy <= r.trash.y2
            ) {
                return
            }
            if (this.isOnTick(r, sx, sy, 10)) {
                this.pendingTickCommitAlertId = r.alertId
                return
            }
            if (this.isOnActionChip(r, sx, sy)) {
                return
            }
            const onPill =
                r.pill && sx >= r.pill.x1 && sx <= r.pill.x2 && sy >= r.pill.y1 && sy <= r.pill.y2
            const onPricePill =
                r.pricePill &&
                sx >= r.pricePill.x1 &&
                sx <= r.pricePill.x2 &&
                sy >= r.pricePill.y1 &&
                sy <= r.pricePill.y2
            const onHandle =
                r.handle &&
                Math.hypot(sx - r.handle.cx, sy - r.handle.cy) <=
                    r.handle.r + 6 * this.horizontalPixelRatio
            const onLine =
                sy >= r.lineY1 &&
                sy <= r.lineY2 &&
                sx >= 0 &&
                sx <= plotRightBmp &&
                !onPill &&
                !onPricePill
            if (r.editable && (onLine || onHandle) && !this.isOnActionChip(r, sx, sy)) {
                event.preventDefault()
                event.stopPropagation()
                this.draggingAlertId = r.alertId
                this.alertLineDragStartPaneY = chartY
                this.alertLineDragThresholdPassed = false
                const alert = this.getAlertById(r.alertId)
                const p = alert ? this.getDisplayPrice(alert) : undefined
                this.dragPreviewPrice = p ?? null
                this.setDragScrollLock(true)
                this.attachDocDragListeners()
                this.updateDrawing()
                return
            }
        }

        const tsW = this.getTimeScaleWidth()
        if (chartX <= tsW) {
            const pr = plotRightBmp
            const lineBand =
                (this.deferAlertEdits() ? 16 : ALERT_LINE_HIT_BAND_LOGICAL) * this.verticalPixelRatio
            let best: { e: LayoutEntry; d: number } | null = null
            for (const e of this.lastLayout?.entries ?? []) {
                const d = Math.abs(sy - e.y)
                if (d <= lineBand && sx >= 0 && sx <= pr) {
                    if (!best || d < best.d) {
                        best = { e, d }
                    }
                }
            }
            if (best) {
                const rEditable = this.isAlertActive(best.e.alert) && isDirectSecurityPriceAlert(best.e.alert)
                if (rEditable) {
                    event.preventDefault()
                    event.stopPropagation()
                    this.draggingAlertId = best.e.alert.id
                    this.alertLineDragStartPaneY = chartY
                    this.alertLineDragThresholdPassed = false
                    this.dragPreviewPrice = this.getDisplayPrice(best.e.alert) ?? null
                    this.setDragScrollLock(true)
                    this.attachDocDragListeners()
                    this.updateDrawing()
                }
            }
        }
    }

    /** Once true for the current line drag, pointer Y updates the preview price. */
    private hasAlertLineDragPassedThreshold(chartY: number): boolean {
        if (this.alertLineDragThresholdPassed) return true
        if (this.alertLineDragStartPaneY == null) return true
        if (Math.abs(chartY - this.alertLineDragStartPaneY) > ALERT_LINE_DRAG_THRESHOLD_PX) {
            this.alertLineDragThresholdPassed = true
            return true
        }
        return false
    }

    private handleDocPointerMove(e: MouseEvent) {
        if (!this.draggingAlertId || !this._chartBounds) return
        const chartY = this.getClampedDragChartY(e.clientY)
        if (!this.hasAlertLineDragPassedThreshold(chartY)) return
        const raw = this.getNearestDragPriceForChartY(chartY)
        if (raw == null) return
        this.dragPreviewPrice = this.roundDraggedThreshold(raw)
        this.updateDrawing()
    }

    private handleDocPointerUp(_e: MouseEvent) {
        this.finishDrag()
    }

    private handleDocTouchMove(e: TouchEvent) {
        if (!this.draggingAlertId || !this._chartBounds) return
        const t = e.touches[0]
        if (!t) return
        const chartY = this.getClampedDragChartY(t.clientY)
        if (!this.hasAlertLineDragPassedThreshold(chartY)) return
        e.preventDefault()
        e.stopPropagation()
        const raw = this.getNearestDragPriceForChartY(chartY)
        if (raw == null) return
        this.dragPreviewPrice = this.roundDraggedThreshold(raw)
        this.updateDrawing()
    }

    /** Keep drag active to pane edge instead of dropping movement near bottom clip. */
    private getClampedDragChartY(clientY: number): number {
        const y = clientY - this._chartBounds!.top
        // Pane bounds here are already pane-local; do not subtract time-scale height.
        return Math.max(0, Math.min(this._chartBounds!.height, y))
    }

    /** Pane-local Y for hover/hit-testing; null only when pointer is outside pane vertically. */
    private getPaneChartYForHitTesting(clientY: number): number | null {
        if (!this._chartBounds) return null
        if (clientY < this._chartBounds.top || clientY > this._chartBounds.bottom) return null
        return Math.max(0, Math.min(this._chartBounds.height, clientY - this._chartBounds.top))
    }

    /**
     * `coordinateToPrice` can return null at extreme pane edges on some scales;
     * use nearest valid edge coordinate so dragging still reaches full bottom.
     */
    private getNearestDragPriceForChartY(chartY: number): number | null {
        const direct = this.coordinateToPrice(chartY as Coordinate)
        if (direct != null) return direct
        for (let delta = 1; delta <= 24; delta++) {
            const up = this.coordinateToPrice(Math.max(0, chartY - delta) as Coordinate)
            if (up != null) return up
            const down = this.coordinateToPrice((chartY + delta) as Coordinate)
            if (down != null) return down
        }
        return null
    }

    private handleDocTouchEnd(_e: TouchEvent) {
        this.finishDrag()
    }

    private finishDrag() {
        if (!this.draggingAlertId) return
        const alert = this.getAlertById(this.draggingAlertId)
        const prev = alert ? getPrimaryChartAlertThresholdPrice(alert) : undefined
        const next = this.dragPreviewPrice
        const didPriceChange =
            this.alertLineDragThresholdPassed &&
            alert &&
            isDirectSecurityPriceAlert(alert) &&
            next != null &&
            prev !== undefined &&
            Math.abs(next - prev) > 1e-8
        this.detachDocDragListeners()
        this.setDragScrollLock(false)
        this.suppressPaneTapUntil = Date.now() + 280
        const overlay = this.deferAlertEdits()
        if (overlay) {
            if (didPriceChange && alert && next != null) {
                this.pendingOverlayPriceByAlertId[alert.id] = next
            }
            this.lastDragEndedAt = Date.now()
        } else if (didPriceChange) {
            this._chart.publishEvent(ChartEvents.CHART_ALERT_ON_CHART_PRICE_COMMIT, {
                alert,
                newPrice: next
            })
            this.lastDragEndedAt = Date.now()
        }
        this.draggingAlertId = null
        this.dragPreviewPrice = null
        this.alertLineDragStartPaneY = null
        this.alertLineDragThresholdPassed = false
        this.updateDrawing()
    }

    private drawLineEndArrow(
        ctx: CanvasRenderingContext2D,
        y: number,
        color: string,
        hRatio: number,
        vRatio: number
    ) {
        const arrowW = 7 * hRatio
        const arrowH = 10 * vRatio
        const tipX = this.canvasWidth - 2 * hRatio
        const baseX = tipX - arrowW

        ctx.save()
        ctx.fillStyle = color
        ctx.strokeStyle = color
        ctx.lineJoin = 'round'
        ctx.lineCap = 'round'
        ctx.lineWidth = Math.max(1, 1.4 * hRatio)
        ctx.beginPath()
        ctx.moveTo(tipX, y)
        ctx.lineTo(baseX, y - arrowH / 2)
        ctx.lineTo(baseX, y + arrowH / 2)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
        ctx.restore()
    }

    private drawOverlayCollapsedAlarmBadge(
        ctx: CanvasRenderingContext2D,
        cx: number,
        y: number,
        hRatio: number,
        vRatio: number,
        palette: { background: { paper: string }; divider: string; text: { primary: string } }
    ) {
        const r = 16 * hRatio
        ctx.save()
        ctx.beginPath()
        ctx.fillStyle = palette.background.paper
        ctx.strokeStyle = alpha(palette.divider, 0.55)
        ctx.lineWidth = hRatio
        ctx.arc(cx, y, r, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        const alarmPath =
            'M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z'
        const sz = 14 * hRatio
        ctx.translate(cx - sz / 2, y - sz / 2)
        ctx.scale(sz / 24, sz / 24)
        ctx.fillStyle = alpha(palette.text.primary, 0.85)
        if (typeof Path2D !== 'undefined') {
            ctx.fill(new Path2D(alarmPath))
        }
        ctx.restore()
    }

    private drawCheckMarkIcon(
        ctx: CanvasRenderingContext2D,
        cx: number,
        cy: number,
        hRatio: number,
        vRatio: number,
        color: string,
        size = 11
    ) {
        const sz = size * Math.min(hRatio, vRatio)
        ctx.save()
        ctx.translate(cx - sz / 2, cy - sz / 2)
        ctx.scale(sz / 24, sz / 24)
        ctx.fillStyle = color
        const d = 'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'
        if (typeof Path2D !== 'undefined') {
            ctx.fill(new Path2D(d))
        }
        ctx.restore()
    }

    private drawPencilIcon(
        ctx: CanvasRenderingContext2D,
        cx: number,
        cy: number,
        hRatio: number,
        vRatio: number,
        color: string
    ) {
        const sz = 12 * Math.min(hRatio, vRatio)
        ctx.save()
        ctx.translate(cx - sz / 2, cy - sz / 2)
        ctx.scale(sz / 24, sz / 24)
        ctx.fillStyle = color
        const d =
            'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'
        if (typeof Path2D !== 'undefined') {
            ctx.fill(new Path2D(d))
        }
        ctx.restore()
    }

    private drawDragHandleDots(
        ctx: CanvasRenderingContext2D,
        cx: number,
        cy: number,
        hRatio: number,
        vRatio: number,
        color: string,
        scale = 1
    ) {
        const dx = 3.2 * hRatio * scale
        const dy = 3.2 * vRatio * scale
        const dotR = 1.35 * Math.min(hRatio, vRatio) * scale
        ctx.fillStyle = color
        for (let col = 0; col < 2; col++) {
            for (let row = 0; row < 3; row++) {
                const px = cx + (col - 0.5) * dx
                const py = cy + (row - 1) * dy
                ctx.beginPath()
                ctx.arc(px, py, dotR, 0, Math.PI * 2)
                ctx.fill()
            }
        }
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        const alerts = this.getRenderableAlerts()
        this.hitRegions = []
        this.lastLayout = null
        if (!alerts?.length) return

        const vRatio = this.verticalPixelRatio
        const hRatio = this.horizontalPixelRatio
        const w = this.canvasWidth
        const centerX = w / 2
        const plotRight = scaledCoordinate(this.getTimeScaleWidth(), hRatio)
        const { palette } = this._chart.theme
        const {
            lineActive,
            lineInactive,
            lineActiveEmphasis,
            lineInactiveEmphasis,
        } = this.getAlertLineColors()
        const pillBgActive = alpha(palette.background.paper, 0.96)
        const pillBgInactive = alpha(palette.background.paper, 0.72)
        const pillTextActive = palette.text.primary
        const pillTextInactive = alpha(palette.text.secondary, 0.9)
        const actionBtnDefaultBg = 'transparent'
        const restartIconColor = palette.text.primary
        const trashIconColor = alpha(palette.text.primary, 0.85)
        const overlayDeleteIconColor = palette.error?.main ?? '#e53935'
        const handleFillActive = palette.primary.main
        const handleFillInactive = alpha(palette.text.secondary, 0.55)
        const handleStroke = palette.background.default

        type Row = { alert: UserAlert; price: number; y: number }
        const rows: Row[] = []
        const drawingAlerts = alerts.filter(isDrawingAlert)
        for (const alert of alerts) {
            if (isDrawingAlert(alert)) continue
            const price = this.getDisplayPrice(alert)
            if (price === undefined) continue
            const yMedia = this.priceToCoordinate(price)
            if (yMedia == null) continue
            const y = scaledCoordinate(yMedia, vRatio)
            rows.push({ alert, price, y })
        }
        rows.sort((a, b) => a.y - b.y)

        const entries: LayoutEntry[] = rows.map((r) => ({
            alert: r.alert,
            price: r.price,
            y: r.y,
            expanded: this.isUiExpanded(r.alert.id)
        }))

        this.lastLayout = { plotRight, entries, centerX, canvasW: w }

        const overlayUi = this.deferAlertEdits()
        for (const e of entries) {
            const serverActive = this.isAlertActive(e.alert)
            const expanded = e.expanded
            const lineColor = expanded
                ? serverActive
                    ? lineActiveEmphasis
                    : lineInactiveEmphasis
                : serverActive
                  ? lineActive
                  : lineInactive
            const lineW = expanded ? Math.max(1.5, hRatio * 1.2) : Math.max(1, hRatio)

            ctx.save()
            const largeDash = Math.max(6, 6 * lineW)
            ctx.setLineDash([largeDash, largeDash])
            ctx.strokeStyle = lineColor
            ctx.lineWidth = lineW
            ctx.beginPath()
            ctx.moveTo(0, e.y)
            ctx.lineTo(w, e.y)
            ctx.stroke()
            ctx.setLineDash([])
            ctx.restore()
            if (!expanded && !overlayUi) {
                this.drawLineEndArrow(ctx, e.y, lineColor, hRatio, vRatio)
            }

            if (!expanded && overlayUi) {
                this.drawOverlayCollapsedAlarmBadge(ctx, centerX, e.y, hRatio, vRatio, palette)
            }
        }

        let drewOverlayHint = false

        for (const e of entries) {
            if (!e.expanded) continue

            const alert = e.alert
            const y = e.y
            const serverActive = this.isAlertActive(alert)
            const lineColor = serverActive ? lineActiveEmphasis : lineInactiveEmphasis

            if (overlayUi) {
                const editable = serverActive && isDirectSecurityPriceAlert(alert)
                // Match Android overview alert chip: min 48×28, 16px icon, 6px vertical padding.
                const chipW = 48 * hRatio
                const chipH = 28 * vRatio
                const chipR = chipH / 2
                const chipIcon = 16
                const edgePad = 16 * hRatio
                const actionX = edgePad
                const tickX = plotRight - edgePad - chipW
                const actionCenterX = actionX + chipW / 2
                const tickCenterX = tickX + chipW / 2
                const chipY = y - chipH / 2
                const isDraggingThis = this.draggingAlertId === alert.id
                const isTemporary =
                    this.temporaryAlert?.id === alert.id ||
                    this.isClientChartTemporaryAlertId(alert.id)
                const showRestart = this.showRestartAction(alert) && !isDraggingThis
                const showDelete = !isTemporary && !showRestart && !isDraggingThis
                const showTick = !isDraggingThis
                const primaryFill = palette.text.primary
                const primaryFg = palette.background.paper

                const fillChip = (x: number, fill: string, stroke: string) => {
                    ctx.fillStyle = fill
                    ctx.strokeStyle = stroke
                    ctx.lineWidth = hRatio
                    this.roundRect(ctx, x, chipY, chipW, chipH, chipR)
                    ctx.fill()
                    ctx.stroke()
                }

                ctx.save()
                if (showRestart) {
                    const restartStyle = this.getActionButtonStyle(
                        this.hoveredRestartAlertId === alert.id,
                        palette,
                        palette.background.paper,
                        restartIconColor
                    )
                    fillChip(actionX, restartStyle.bg, restartStyle.border)
                    this.drawPlayIcon(ctx, actionCenterX, y, hRatio, vRatio, restartStyle.icon, chipIcon)
                } else if (showDelete) {
                    const trashStyle = this.getActionButtonStyle(
                        this.hoveredTrashAlertId === alert.id,
                        palette,
                        palette.background.paper,
                        overlayDeleteIconColor
                    )
                    fillChip(actionX, trashStyle.bg, trashStyle.border)
                    this.drawTrashIcon(ctx, actionCenterX, y, hRatio, vRatio, trashStyle.icon, chipIcon)
                }
                ctx.restore()

                if (showTick) {
                    ctx.save()
                    fillChip(tickX, primaryFill, primaryFill)
                    this.drawCheckMarkIcon(
                        ctx,
                        tickCenterX,
                        y + 0.5 * vRatio,
                        hRatio,
                        vRatio,
                        primaryFg,
                        chipIcon
                    )
                    ctx.restore()
                }

                const handlePillW = chipW
                const handlePillH = chipH
                const handlePillX = centerX - handlePillW / 2
                const handlePillY = chipY
                ctx.fillStyle = palette.background.paper
                ctx.strokeStyle = alpha(palette.divider, 0.65)
                ctx.lineWidth = hRatio
                this.roundRect(ctx, handlePillX, handlePillY, handlePillW, handlePillH, chipR)
                ctx.fill()
                ctx.stroke()
                this.drawDragHandleDots(ctx, centerX, y, hRatio, vRatio, alpha(palette.text.secondary, 0.62), 1.35)

                ctx.font = `${13 * vRatio}px sans-serif`
                const padX = 10 * hRatio
                const pencilSlot = 16 * hRatio
                const priceParts = getChartAlertLineTagParts(this.formatPrice(e.price), alert)
                const maxPriceTextWidth = Math.min(480 * hRatio, w * 0.85) - padX * 2 - pencilSlot
                const priceTagParts = truncateChartAlertLineTagParts(
                    priceParts,
                    maxPriceTextWidth,
                    (text) => ctx.measureText(text).width
                )
                const tw = measureChartAlertLineTagWidth(
                    priceTagParts,
                    (text) => ctx.measureText(text).width
                )
                const pricePillW = tw + padX * 2 + pencilSlot
                const pricePillH = chipH
                const gapPriceToHandle = 6 * vRatio
                const pricePillX = centerX - pricePillW / 2
                const pricePillY = handlePillY - gapPriceToHandle - pricePillH
                ctx.fillStyle = primaryFill
                ctx.strokeStyle = primaryFill
                ctx.lineWidth = hRatio
                this.roundRect(ctx, pricePillX, pricePillY, pricePillW, pricePillH, chipR)
                ctx.fill()
                ctx.stroke()
                ctx.fillStyle = primaryFg
                ctx.textBaseline = 'middle'
                ctx.textAlign = 'left'
                this.drawChartAlertLineTagText(
                    ctx,
                    priceTagParts,
                    alert,
                    pricePillX + padX,
                    pricePillY + pricePillH / 2,
                    primaryFg,
                    palette,
                    palette.mode === 'light'
                )
                this.drawPencilIcon(
                    ctx,
                    pricePillX + pricePillW - padX - 7 * hRatio,
                    pricePillY + pricePillH / 2,
                    hRatio,
                    vRatio,
                    primaryFg
                )
                ctx.textAlign = 'left'

                if (this._chart.touchEnabled && !drewOverlayHint) {
                    drewOverlayHint = true
                    const hint = 'Tap outside the alert slider to cancel.'
                    ctx.font = `${13 * vRatio}px sans-serif`
                    ctx.fillStyle = alpha(palette.text.secondary, 0.38)
                    ctx.textAlign = 'center'
                    ctx.textBaseline = 'bottom'
                    ctx.fillText(hint, centerX, this.canvasHeight - 10 * vRatio)
                    ctx.textAlign = 'left'
                    ctx.textBaseline = 'middle'
                }

                const padHit = 4 * hRatio
                const actionHit = {
                    x1: actionX - padHit,
                    y1: chipY - padHit,
                    x2: actionX + chipW + padHit,
                    y2: chipY + chipH + padHit
                }
                this.hitRegions.push({
                    alertId: alert.id,
                    trash: showDelete ? actionHit : null,
                    restart: showRestart ? actionHit : null,
                    pill: null,
                    lineY1: chipY,
                    lineY2: chipY + chipH,
                    handle: { cx: centerX, cy: y, r: Math.max(chipW, chipH) / 2 },
                    tick: showTick
                        ? { cx: tickCenterX, cy: y, r: Math.max(chipW, chipH) / 2 }
                        : null,
                    tickBox: showTick
                        ? {
                            x1: tickX - padHit,
                            y1: chipY - padHit,
                            x2: tickX + chipW + padHit,
                            y2: chipY + chipH + padHit
                        }
                        : null,
                    pricePill: { x1: pricePillX, y1: pricePillY, x2: pricePillX + pricePillW, y2: pricePillY + pricePillH },
                    editable
                })
                continue
            }

            const msg = this.expandedPillTextByAlertId[alert.id] ?? alert.message ?? alert.name ?? 'Alert'
            const msgParts = getChartAlertLineTagParts(msg, alert)

            const paddingX = 10 * hRatio
            ctx.font = `${12 * vRatio}px sans-serif`
            const actionW = 28 * hRatio
            const showRestart = this.showRestartAction(alert)
            const maxPillW = Math.min(480 * hRatio, w * 0.85)
            const maxTextWidth = maxPillW - paddingX * 2 - actionW
            const truncatedMsgParts = truncateChartAlertLineTagParts(
                msgParts,
                maxTextWidth,
                (value) => ctx.measureText(value).width
            )
            const tw = measureChartAlertLineTagWidth(
                truncatedMsgParts,
                (value) => ctx.measureText(value).width
            )
            const pillW = tw + paddingX * 2 + actionW
            const pillH = 24 * vRatio
            const pillX = centerX - pillW / 2
            const pillY = y - pillH / 2

            ctx.fillStyle = serverActive ? pillBgActive : pillBgInactive
            ctx.strokeStyle = lineColor
            ctx.lineWidth = hRatio
            this.roundRect(ctx, pillX, pillY, pillW, pillH, 4 * hRatio)
            ctx.fill()
            ctx.stroke()

            ctx.fillStyle = serverActive ? pillTextActive : pillTextInactive
            ctx.textBaseline = 'middle'
            this.drawChartAlertLineTagText(
                ctx,
                truncatedMsgParts,
                alert,
                pillX + paddingX,
                y,
                serverActive ? pillTextActive : pillTextInactive,
                palette
            )

            const actionX1 = pillX + pillW - actionW
            const actionBtnX = actionX1 + 3 * hRatio
            const actionBtnY = pillY + 3 * vRatio
            const actionBtnW = actionW - 6 * hRatio
            const actionBtnH = pillH - 6 * vRatio
            if (showRestart) {
                const restartStyle = this.getActionButtonStyle(
                    this.hoveredRestartAlertId === alert.id,
                    palette,
                    actionBtnDefaultBg,
                    restartIconColor
                )
                ctx.fillStyle = restartStyle.bg
                this.roundRect(ctx, actionBtnX, actionBtnY, actionBtnW, actionBtnH, 3 * hRatio)
                ctx.fill()
                ctx.strokeStyle = restartStyle.border
                ctx.lineWidth = hRatio
                this.roundRect(ctx, actionBtnX, actionBtnY, actionBtnW, actionBtnH, 3 * hRatio)
                ctx.stroke()
                this.drawPlayIcon(
                    ctx,
                    actionX1 + actionW / 2,
                    y,
                    hRatio,
                    vRatio,
                    restartStyle.icon
                )
            } else {
                const trashStyle = this.getActionButtonStyle(
                    this.hoveredTrashAlertId === alert.id,
                    palette,
                    actionBtnDefaultBg,
                    trashIconColor
                )
                ctx.fillStyle = trashStyle.bg
                this.roundRect(ctx, actionBtnX, actionBtnY, actionBtnW, actionBtnH, 3 * hRatio)
                ctx.fill()
                ctx.strokeStyle = trashStyle.border
                ctx.lineWidth = hRatio
                this.roundRect(ctx, actionBtnX, actionBtnY, actionBtnW, actionBtnH, 3 * hRatio)
                ctx.stroke()
                this.drawTrashIcon(
                    ctx,
                    actionX1 + actionW / 2,
                    y,
                    hRatio,
                    vRatio,
                    trashStyle.icon
                )
            }

            const handleR = 5 * hRatio
            // Match orders-on-chart style offset so the handle remains clearly visible near right edge.
            const handleCx = w - 35 * hRatio
            ctx.beginPath()
            ctx.fillStyle = serverActive ? handleFillActive : handleFillInactive
            ctx.arc(handleCx, y, handleR, 0, Math.PI * 2)
            ctx.fill()
            ctx.strokeStyle = handleStroke
            ctx.lineWidth = hRatio
            ctx.stroke()

            const lineBand = ALERT_LINE_HIT_BAND_LOGICAL * vRatio
            const editable = serverActive && isDirectSecurityPriceAlert(alert)
            this.hitRegions.push({
                alertId: alert.id,
                trash: showRestart
                    ? null
                    : {
                        x1: actionBtnX,
                        y1: actionBtnY,
                        x2: actionBtnX + actionBtnW,
                        y2: actionBtnY + actionBtnH
                    },
                restart: showRestart
                    ? {
                        x1: actionBtnX,
                        y1: actionBtnY,
                        x2: actionBtnX + actionBtnW,
                        y2: actionBtnY + actionBtnH
                    }
                    : null,
                pill: { x1: pillX, y1: pillY, x2: pillX + pillW - actionW, y2: pillY + pillH },
                lineY1: y - lineBand,
                lineY2: y + lineBand,
                handle: { cx: handleCx, cy: y, r: handleR },
                tick: null,
                pricePill: null,
                editable
            })
        }

        for (const e of entries) {
            if (e.expanded) continue
            const lineBand = ALERT_LINE_HIT_BAND_LOGICAL * vRatio
            const editable = this.isAlertActive(e.alert) && isDirectSecurityPriceAlert(e.alert)
            this.hitRegions.push({
                alertId: e.alert.id,
                trash: null,
                restart: null,
                pill: null,
                lineY1: e.y - lineBand,
                lineY2: e.y + lineBand,
                handle: null,
                tick: null,
                pricePill: null,
                editable
            })
        }

        this.drawDrawingAlertOverlays(
            ctx,
            drawingAlerts,
            hRatio,
            vRatio,
            w,
            centerX,
            plotRight,
            {
                lineActive,
                lineInactive,
                lineActiveEmphasis,
                lineInactiveEmphasis,
                pillBgActive,
                pillBgInactive,
                pillTextActive,
                pillTextInactive,
                overlayUi,
                palette,
            }
        )
    }

    private drawDrawingAlertOverlays(
        ctx: CanvasRenderingContext2D,
        drawingAlerts: UserAlert[],
        hRatio: number,
        vRatio: number,
        canvasW: number,
        centerX: number,
        plotRight: number,
        colors: {
            lineActive: string
            lineInactive: string
            lineActiveEmphasis: string
            lineInactiveEmphasis: string
            pillBgActive: string
            pillBgInactive: string
            pillTextActive: string
            pillTextInactive: string
            overlayUi: boolean
            palette: ChartAlertsOnChartTool['_chart']['theme']['palette']
        }
    ) {
        if (!drawingAlerts.length) return
        const {
            lineActive,
            lineInactive,
            lineActiveEmphasis,
            lineInactiveEmphasis,
            pillBgActive,
            pillBgInactive,
            pillTextActive,
            pillTextInactive,
            overlayUi,
            palette,
        } = colors
        const timeZone = this._chart.timezone
        const lineBand = ALERT_LINE_HIT_BAND_LOGICAL * vRatio

        for (const alert of drawingAlerts) {
            const drawing = getDrawingFromAlert(alert)
            const points = drawing?.points ?? []
            if (points.length < 1) continue

            const isHorizontal = drawing?.drawingType === 'horizontalLine' || drawing?.drawingType === 'horizontalRay'
            const isRectangle = drawing?.drawingType === 'rectangle'
            const isFibonacci = drawing?.drawingType === 'fibonacci'
            const isChannel = isChannelAlertDrawingType(drawing?.drawingType)

            if (isChannel && points.length >= 4) {
                const channelPts = [0, 1, 2, 3].map((i) => {
                    const chartTime = alertTimestampMsToChartTime(Number(points[i].timestamp), timeZone)
                    const xMedia = this.timeToCoordinate(chartTime)
                    const yMedia = this.priceToCoordinate(points[i].price ?? 0)
                    if (xMedia == null || yMedia == null) return null
                    return {
                        x: scaledCoordinate(xMedia, hRatio),
                        y: scaledCoordinate(yMedia, vRatio),
                    }
                })
                if (channelPts.every((p): p is { x: number; y: number } => p != null)) {
                    const [c1, c2, c3, c4] = channelPts
                    const expanded = this.isUiExpanded(alert.id)
                    const serverActive = this.isAlertActive(alert)
                    const lineColor = expanded
                        ? serverActive
                            ? lineActiveEmphasis
                            : lineInactiveEmphasis
                        : serverActive
                          ? lineActive
                          : lineInactive
                    const lineW = expanded ? Math.max(1.5, hRatio * 1.2) : Math.max(1, hRatio)

                    ctx.save()
                    ctx.globalAlpha = 0.12
                    ctx.fillStyle = lineColor
                    ctx.beginPath()
                    ctx.moveTo(c1.x, c1.y)
                    ctx.lineTo(c2.x, c2.y)
                    ctx.lineTo(c4.x, c4.y)
                    ctx.lineTo(c3.x, c3.y)
                    ctx.closePath()
                    ctx.fill()
                    ctx.globalAlpha = 1
                    const largeDash = Math.max(6, 6 * lineW)
                    ctx.setLineDash([largeDash, largeDash])
                    ctx.strokeStyle = lineColor
                    ctx.lineWidth = lineW
                    ctx.beginPath()
                    ctx.moveTo(c1.x, c1.y)
                    ctx.lineTo(c2.x, c2.y)
                    ctx.moveTo(c3.x, c3.y)
                    ctx.lineTo(c4.x, c4.y)
                    ctx.stroke()
                    ctx.setLineDash([])
                    ctx.restore()

                    const midX = (c1.x + c2.x + c3.x + c4.x) / 4
                    const midY = (c1.y + c2.y + c3.y + c4.y) / 4
                    const ys = [c1.y, c2.y, c3.y, c4.y]
                    const bandTop = Math.min(...ys)
                    const bandBottom = Math.max(...ys)
                    const channelSegments = [
                        { x1: c1.x, y1: c1.y, x2: c2.x, y2: c2.y },
                        { x1: c3.x, y1: c3.y, x2: c4.x, y2: c4.y },
                    ]

                    if (!expanded && !overlayUi) {
                        const msg = alert.message ?? alert.name ?? 'Drawing alert'
                        const msgParts = getChartAlertLineTagParts(msg, alert)
                        const paddingX = 10 * hRatio
                        ctx.font = `${12 * vRatio}px sans-serif`
                        const maxPillW = Math.min(480 * hRatio, canvasW * 0.85)
                        const truncatedMsgParts = truncateChartAlertLineTagParts(
                            msgParts,
                            maxPillW - paddingX * 2,
                            (value) => ctx.measureText(value).width
                        )
                        const tw = measureChartAlertLineTagWidth(
                            truncatedMsgParts,
                            (text) => ctx.measureText(text).width
                        )
                        const pillW = tw + paddingX * 2
                        const pillH = 22 * vRatio
                        const pillX = Math.min(Math.max(midX - pillW / 2, 4 * hRatio), canvasW - pillW - 4 * hRatio)
                        const pillY = midY - pillH / 2
                        ctx.fillStyle = serverActive ? pillBgActive : pillBgInactive
                        this.roundRect(ctx, pillX, pillY, pillW, pillH, 4 * hRatio)
                        ctx.fill()
                        this.drawChartAlertLineTagText(
                            ctx,
                            truncatedMsgParts,
                            alert,
                            pillX + paddingX,
                            pillY + pillH / 2,
                            serverActive ? pillTextActive : pillTextInactive,
                            palette
                        )
                        this.hitRegions.push({
                            alertId: alert.id,
                            trash: null,
                            restart: null,
                            pill: { x1: pillX, y1: pillY, x2: pillX + pillW, y2: pillY + pillH },
                            lineY1: bandTop - lineBand,
                            lineY2: bandBottom + lineBand,
                            handle: null,
                            tick: null,
                            pricePill: null,
                            editable: false,
                            channelSegments,
                        })
                        continue
                    }

                    this.hitRegions.push({
                        alertId: alert.id,
                        trash: null,
                        restart: null,
                        pill: null,
                        lineY1: bandTop - lineBand,
                        lineY2: bandBottom + lineBand,
                        handle: null,
                        tick: null,
                        pricePill: null,
                        editable: false,
                        channelSegments,
                    })
                    continue
                }
            }

            const chartTime1 = alertTimestampMsToChartTime(Number(points[0].timestamp), timeZone)
            const chartTime2 = points[1]
                ? alertTimestampMsToChartTime(Number(points[1].timestamp), timeZone)
                : chartTime1
            const x1Media = this.timeToCoordinate(chartTime1)
            const x2Media = isHorizontal || isFibonacci ? this.getTimeScaleWidth() : this.timeToCoordinate(chartTime2)
            let overlayPrice1 = points[0].price ?? 0
            let overlayPrice2 = isHorizontal
                ? overlayPrice1
                : (points[1]?.price ?? points[0].price ?? 0)
            if (isFibonacci && points.length >= 2) {
                const level = parseFibonacciAlertLevel(getFibonacciAlertLevelFromDrawing(drawing?.options))
                const operator = alert.condition?.securityAlertCondition?.operator
                if (isZoneOperator(operator)) {
                    const { upperLevel, lowerLevel } = getFibonacciChannelBoundLevelsFromOptions(
                        drawing?.options,
                        points[0].price,
                        points[1].price
                    )
                    overlayPrice1 = getFibonacciLevelPrice(points[0].price ?? 0, points[1].price ?? 0, upperLevel)
                    overlayPrice2 = getFibonacciLevelPrice(points[0].price ?? 0, points[1].price ?? 0, lowerLevel)
                } else {
                    overlayPrice1 = getFibonacciLevelPrice(points[0].price ?? 0, points[1].price ?? 0, level)
                    overlayPrice2 = overlayPrice1
                }
            }
            const y1Media = this.priceToCoordinate(overlayPrice1)
            const y2Media = this.priceToCoordinate(overlayPrice2)
            if (x1Media == null || x2Media == null || y1Media == null || y2Media == null) continue

            const x1 = scaledCoordinate(x1Media, hRatio)
            const x2 = scaledCoordinate(x2Media, hRatio)
            const y1 = scaledCoordinate(y1Media, vRatio)
            const y2 = scaledCoordinate(y2Media, vRatio)

            const expanded = this.isUiExpanded(alert.id)
            const serverActive = this.isAlertActive(alert)
            const lineColor = expanded
                ? serverActive
                    ? lineActiveEmphasis
                    : lineInactiveEmphasis
                : serverActive
                  ? lineActive
                  : lineInactive
            const lineW = expanded ? Math.max(1.5, hRatio * 1.2) : Math.max(1, hRatio)

            if (isFibonacci) {
                const bandTop = Math.min(y1, y2)
                const bandBottom = Math.max(y1, y2)
                const drawX1 = 0
                const drawX2 = canvasW

                ctx.save()
                if (bandBottom - bandTop > 1) {
                    ctx.globalAlpha = 0.12
                    ctx.fillStyle = lineColor
                    ctx.fillRect(drawX1, bandTop, drawX2 - drawX1, bandBottom - bandTop)
                    ctx.globalAlpha = 1
                }
                const largeDash = Math.max(6, 6 * lineW)
                ctx.setLineDash([largeDash, largeDash])
                ctx.strokeStyle = lineColor
                ctx.lineWidth = lineW
                ctx.beginPath()
                ctx.moveTo(drawX1, y1)
                ctx.lineTo(drawX2, y1)
                ctx.stroke()
                if (Math.abs(y2 - y1) > 1) {
                    ctx.beginPath()
                    ctx.moveTo(drawX1, y2)
                    ctx.lineTo(drawX2, y2)
                    ctx.stroke()
                }
                ctx.setLineDash([])
                ctx.restore()

                const midY = (y1 + y2) / 2
                if (!expanded && !overlayUi) {
                    const msg = alert.message ?? alert.name ?? 'Drawing alert'
                    const msgParts = getChartAlertLineTagParts(msg, alert)
                    const paddingX = 10 * hRatio
                    ctx.font = `${12 * vRatio}px sans-serif`
                    const maxPillW = Math.min(480 * hRatio, canvasW * 0.85)
                    const truncatedMsgParts = truncateChartAlertLineTagParts(
                        msgParts,
                        maxPillW - paddingX * 2,
                        (value) => ctx.measureText(value).width
                    )
                    const tw = measureChartAlertLineTagWidth(
                        truncatedMsgParts,
                        (text) => ctx.measureText(text).width
                    )
                    const pillW = tw + paddingX * 2
                    const pillH = 22 * vRatio
                    const pillX = Math.min(Math.max(centerX - pillW / 2, 4 * hRatio), canvasW - pillW - 4 * hRatio)
                    const pillY = midY - pillH / 2
                    ctx.fillStyle = serverActive ? pillBgActive : pillBgInactive
                    this.roundRect(ctx, pillX, pillY, pillW, pillH, 4 * hRatio)
                    ctx.fill()
                    this.drawChartAlertLineTagText(
                        ctx,
                        truncatedMsgParts,
                        alert,
                        pillX + paddingX,
                        pillY + pillH / 2,
                        serverActive ? pillTextActive : pillTextInactive,
                        palette
                    )
                    this.hitRegions.push({
                        alertId: alert.id,
                        trash: null,
                        restart: null,
                        pill: { x1: pillX, y1: pillY, x2: pillX + pillW, y2: pillY + pillH },
                        lineY1: bandTop - lineBand,
                        lineY2: bandBottom + lineBand,
                        handle: null,
                        tick: null,
                        pricePill: null,
                        editable: false,
                        trendlineSegment: { x1: drawX1, y1: midY, x2: drawX2, y2: midY },
                    })
                    continue
                }

                this.hitRegions.push({
                    alertId: alert.id,
                    trash: null,
                    restart: null,
                    pill: null,
                    lineY1: bandTop - lineBand,
                    lineY2: bandBottom + lineBand,
                    handle: null,
                    tick: null,
                    pricePill: null,
                    editable: false,
                    trendlineSegment: { x1: drawX1, y1: midY, x2: drawX2, y2: midY },
                })
                continue
            }

            if (isRectangle) {
                const rectLeft = Math.min(x1, x2)
                const rectRight = Math.max(x1, x2)
                const rectTop = Math.min(y1, y2)
                const rectBottom = Math.max(y1, y2)
                const rectW = Math.max(1, rectRight - rectLeft)
                const rectH = Math.max(1, rectBottom - rectTop)

                ctx.save()
                const largeDash = Math.max(6, 6 * lineW)
                ctx.setLineDash([largeDash, largeDash])
                ctx.strokeStyle = lineColor
                ctx.lineWidth = lineW
                ctx.strokeRect(rectLeft, rectTop, rectW, rectH)
                ctx.setLineDash([])
                ctx.restore()

                const midX = (rectLeft + rectRight) / 2
                const midY = (rectTop + rectBottom) / 2

                if (!expanded && !overlayUi) {
                    const msg = alert.message ?? alert.name ?? 'Drawing alert'
                    const msgParts = getChartAlertLineTagParts(msg, alert)
                    const paddingX = 10 * hRatio
                    ctx.font = `${12 * vRatio}px sans-serif`
                    const maxPillW = Math.min(480 * hRatio, canvasW * 0.85)
                    const truncatedMsgParts = truncateChartAlertLineTagParts(
                        msgParts,
                        maxPillW - paddingX * 2,
                        (value) => ctx.measureText(value).width
                    )
                    const tw = measureChartAlertLineTagWidth(
                        truncatedMsgParts,
                        (text) => ctx.measureText(text).width
                    )
                    const pillW = tw + paddingX * 2
                    const pillH = 22 * vRatio
                    const pillX = Math.min(Math.max(midX - pillW / 2, 4 * hRatio), canvasW - pillW - 4 * hRatio)
                    const pillY = midY - pillH / 2
                    ctx.fillStyle = serverActive ? pillBgActive : pillBgInactive
                    this.roundRect(ctx, pillX, pillY, pillW, pillH, 4 * hRatio)
                    ctx.fill()
                    this.drawChartAlertLineTagText(
                        ctx,
                        truncatedMsgParts,
                        alert,
                        pillX + paddingX,
                        pillY + pillH / 2,
                        serverActive ? pillTextActive : pillTextInactive,
                        palette
                    )
                    this.hitRegions.push({
                        alertId: alert.id,
                        trash: null,
                        restart: null,
                        pill: { x1: pillX, y1: pillY, x2: pillX + pillW, y2: pillY + pillH },
                        lineY1: rectTop - lineBand,
                        lineY2: rectBottom + lineBand,
                        handle: null,
                        tick: null,
                        pricePill: null,
                        editable: false,
                        rectangleRegion: { x1: rectLeft, y1: rectTop, x2: rectRight, y2: rectBottom },
                    })
                    continue
                }

                this.hitRegions.push({
                    alertId: alert.id,
                    trash: null,
                    restart: null,
                    pill: null,
                    lineY1: rectTop - lineBand,
                    lineY2: rectBottom + lineBand,
                    handle: null,
                    tick: null,
                    pricePill: null,
                    editable: false,
                    rectangleRegion: { x1: rectLeft, y1: rectTop, x2: rectRight, y2: rectBottom },
                })
                continue
            }

            const drawingType = drawing?.drawingType ?? ''
            const canvasH = ctx.canvas?.height ?? 0
            let drawX1 = x1
            let drawY1 = y1
            let drawX2 = x2
            let drawY2 = y2
            if (isHorizontal) {
                // horizontalLine: full width; horizontalRay: from anchor to the right
                drawX1 = drawingType === 'horizontalRay' ? x1 : 0
                drawY1 = y1
                drawX2 = canvasW
                drawY2 = y1
            } else if (x2 !== x1) {
                const slope = (y2 - y1) / (x2 - x1)
                if (drawingType === 'extendedLine') {
                    drawX1 = 0
                    drawY1 = y1 + slope * (drawX1 - x1)
                    drawX2 = canvasW
                    drawY2 = y1 + slope * (drawX2 - x1)
                } else if (drawingType === 'ray') {
                    // From p1 through p2 to the canvas edge in the p1→p2 direction
                    drawX1 = x1
                    drawY1 = y1
                    drawX2 = x2 >= x1 ? canvasW : 0
                    drawY2 = y1 + slope * (drawX2 - x1)
                }
                // trendLine / arrow / etc. keep the finite p1→p2 segment
            } else if (drawingType === 'extendedLine') {
                drawX1 = x1
                drawX2 = x1
                drawY1 = 0
                drawY2 = canvasH
            } else if (drawingType === 'ray') {
                drawX1 = x1
                drawX2 = x1
                drawY1 = y1
                drawY2 = y2 >= y1 ? canvasH : 0
            }

            ctx.save()
            const largeDash = Math.max(6, 6 * lineW)
            ctx.setLineDash([largeDash, largeDash])
            ctx.strokeStyle = lineColor
            ctx.lineWidth = lineW
            ctx.beginPath()
            ctx.moveTo(drawX1, drawY1)
            ctx.lineTo(drawX2, drawY2)
            ctx.stroke()
            ctx.setLineDash([])
            ctx.restore()

            const midX = (x1 + x2) / 2
            const midY = (y1 + y2) / 2

            if (!expanded && !overlayUi) {
                const msg = alert.message ?? alert.name ?? 'Drawing alert'
                const msgParts = getChartAlertLineTagParts(msg, alert)
                const paddingX = 10 * hRatio
                ctx.font = `${12 * vRatio}px sans-serif`
                const maxPillW = Math.min(480 * hRatio, canvasW * 0.85)
                const truncatedMsgParts = truncateChartAlertLineTagParts(
                    msgParts,
                    maxPillW - paddingX * 2,
                    (value) => ctx.measureText(value).width
                )
                const tw = measureChartAlertLineTagWidth(
                    truncatedMsgParts,
                    (text) => ctx.measureText(text).width
                )
                const pillW = tw + paddingX * 2
                const pillH = 22 * vRatio
                const pillX = Math.min(Math.max(midX - pillW / 2, 4 * hRatio), canvasW - pillW - 4 * hRatio)
                const pillY = midY - pillH / 2
                ctx.fillStyle = serverActive ? pillBgActive : pillBgInactive
                this.roundRect(ctx, pillX, pillY, pillW, pillH, 4 * hRatio)
                ctx.fill()
                this.drawChartAlertLineTagText(
                    ctx,
                    truncatedMsgParts,
                    alert,
                    pillX + paddingX,
                    pillY + pillH / 2,
                    serverActive ? pillTextActive : pillTextInactive,
                    palette
                )
                this.hitRegions.push({
                    alertId: alert.id,
                    trash: null,
                    restart: null,
                    pill: { x1: pillX, y1: pillY, x2: pillX + pillW, y2: pillY + pillH },
                    lineY1: Math.min(drawY1, drawY2) - lineBand,
                    lineY2: Math.max(drawY1, drawY2) + lineBand,
                    handle: null,
                    tick: null,
                    pricePill: null,
                    editable: false,
                    trendlineSegment: { x1: drawX1, y1: drawY1, x2: drawX2, y2: drawY2 },
                })
                continue
            }

            this.hitRegions.push({
                alertId: alert.id,
                trash: null,
                restart: null,
                pill: null,
                lineY1: Math.min(drawY1, drawY2) - lineBand,
                lineY2: Math.max(drawY1, drawY2) + lineBand,
                handle: null,
                tick: null,
                pricePill: null,
                editable: false,
                trendlineSegment: { x1: drawX1, y1: drawY1, x2: drawX2, y2: drawY2 },
            })
        }
    }

    private roundRect(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number
    ) {
        const r = Math.min(radius, width / 2, height / 2)
        ctx.beginPath()
        ctx.moveTo(x + r, y)
        ctx.lineTo(x + width - r, y)
        ctx.quadraticCurveTo(x + width, y, x + width, y + r)
        ctx.lineTo(x + width, y + height - r)
        ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
        ctx.lineTo(x + r, y + height)
        ctx.quadraticCurveTo(x, y + height, x, y + height - r)
        ctx.lineTo(x, y + r)
        ctx.quadraticCurveTo(x, y, x + r, y)
        ctx.closePath()
    }

    private drawChartAlertLineTagText(
        ctx: CanvasRenderingContext2D,
        parts: { primary: string; status?: string },
        alert: UserAlert,
        x: number,
        y: number,
        primaryColor: string,
        palette: {
            mode: 'light' | 'dark'
            text: { secondary: string }
            success?: { dark?: string; main: string }
            warning?: { light?: string; main: string }
        },
        statusOnDarkBackground?: boolean
    ) {
        ctx.textAlign = 'left'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = primaryColor
        ctx.fillText(parts.primary, x, y)
        if (!parts.status) return

        let cursorX = x + ctx.measureText(parts.primary).width
        ctx.fillStyle = primaryColor
        ctx.fillText(CHART_ALERT_STATUS_SEPARATOR, cursorX, y)
        cursorX += ctx.measureText(CHART_ALERT_STATUS_SEPARATOR).width
        ctx.fillStyle = resolveColorForAlertStatus(
            getChartAlertStatusColorLabel(alert, parts.status),
            palette,
            statusOnDarkBackground
        )
        ctx.fillText(parts.status, cursorX, y)
    }

    private drawTrashIcon(
        ctx: CanvasRenderingContext2D,
        cx: number,
        cy: number,
        hRatio: number,
        vRatio: number,
        color: string,
        size = 14
    ) {
        // Match `BinIcon` silhouette used in series metadata (viewBox 18x18).
        const sizeX = size * hRatio
        const sizeY = size * vRatio
        const scaleX = sizeX / 18
        const scaleY = sizeY / 18
        const pathData =
            'M12 4h3v1h-1.04l-.88 9.64a1.5 1.5 0 0 1-1.5 1.36H6.42a1.5 1.5 0 0 1-1.5-1.36L4.05 5H3V4h3v-.5C6 2.67 6.67 2 7.5 2h3c.83 0 1.5.67 1.5 1.5V4ZM7.5 3a.5.5 0 0 0-.5.5V4h4v-.5a.5.5 0 0 0-.5-.5h-3ZM5.05 5l.87 9.55a.5.5 0 0 0 .5.45h5.17a.5.5 0 0 0 .5-.45L12.94 5h-7.9Z'
        ctx.save()
        ctx.translate(cx - sizeX / 2, cy - sizeY / 2)
        ctx.scale(scaleX, scaleY)
        ctx.fillStyle = color
        if (typeof Path2D !== 'undefined') {
            ctx.fill(new Path2D(pathData))
        } else {
            // Fallback (older canvas environments): simple bin-like mark.
            ctx.strokeStyle = color
            ctx.lineWidth = 1
            ctx.strokeRect(5.5, 6, 7, 8)
            ctx.beginPath()
            ctx.moveTo(5, 5)
            ctx.lineTo(13, 5)
            ctx.stroke()
        }
        ctx.restore()
    }

    private drawPlayIcon(
        ctx: CanvasRenderingContext2D,
        cx: number,
        cy: number,
        hRatio: number,
        vRatio: number,
        color: string,
        size = 18
    ) {
        const sizeX = size * hRatio
        const sizeY = size * vRatio
        const scaleX = sizeX / 24
        const scaleY = sizeY / 24
        const pathData =
            'M10 16.5v-9l7 4.5-7 4.5M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z'
        ctx.save()
        ctx.translate(cx - sizeX / 2, cy - sizeY / 2)
        ctx.scale(scaleX, scaleY)
        ctx.fillStyle = color
        if (typeof Path2D !== 'undefined') {
            ctx.fill(new Path2D(pathData))
        } else {
            ctx.beginPath()
            ctx.moveTo(10, 16.5)
            ctx.lineTo(10, 7.5)
            ctx.lineTo(17, 12)
            ctx.closePath()
            ctx.fill()
        }
        ctx.restore()
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const sx = scaledCoordinate(x, this.horizontalPixelRatio)
        const sy = scaledCoordinate(y, this.verticalPixelRatio)
        const plotRightBmp = scaledCoordinate(this.getTimeScaleWidth(), this.horizontalPixelRatio)
        const tsW = this.getTimeScaleWidth()

        for (const r of this.hitRegions) {
            if (
                r.restart &&
                sx >= r.restart.x1 &&
                sx <= r.restart.x2 &&
                sy >= r.restart.y1 &&
                sy <= r.restart.y2
            ) {
                return { externalId: this.id, zOrder: 'top', cursorStyle: 'pointer' }
            }
            if (
                r.trash &&
                sx >= r.trash.x1 &&
                sx <= r.trash.x2 &&
                sy >= r.trash.y1 &&
                sy <= r.trash.y2
            ) {
                return { externalId: this.id, zOrder: 'top', cursorStyle: 'pointer' }
            }
        }
        for (const r of this.hitRegions) {
            if (this.isOnTick(r, sx, sy, 4)) {
                return { externalId: this.id, zOrder: 'top', cursorStyle: 'pointer' }
            }
        }
        for (const r of this.hitRegions) {
            const onPill =
                r.pill && sx >= r.pill.x1 && sx <= r.pill.x2 && sy >= r.pill.y1 && sy <= r.pill.y2
            if (onPill) {
                return { externalId: this.id, zOrder: 'top', cursorStyle: 'pointer' }
            }
        }
        for (const r of this.hitRegions) {
            if (
                r.pricePill &&
                sx >= r.pricePill.x1 &&
                sx <= r.pricePill.x2 &&
                sy >= r.pricePill.y1 &&
                sy <= r.pricePill.y2
            ) {
                return { externalId: this.id, zOrder: 'top', cursorStyle: 'pointer' }
            }
        }
        if (x > tsW) return null
        for (const r of this.hitRegions) {
            if (r.rectangleRegion) {
                const rect = r.rectangleRegion
                const band = ALERT_LINE_HIT_BAND_LOGICAL * this.verticalPixelRatio * 1.5
                const nearLeft = Math.abs(sx - rect.x1) <= band && sy >= rect.y1 - band && sy <= rect.y2 + band
                const nearRight = Math.abs(sx - rect.x2) <= band && sy >= rect.y1 - band && sy <= rect.y2 + band
                const nearTop = Math.abs(sy - rect.y1) <= band && sx >= rect.x1 - band && sx <= rect.x2 + band
                const nearBottom = Math.abs(sy - rect.y2) <= band && sx >= rect.x1 - band && sx <= rect.x2 + band
                if (nearLeft || nearRight || nearTop || nearBottom) {
                    return { externalId: this.id, zOrder: 'top', cursorStyle: 'pointer' }
                }
            }
            if (r.channelSegments) {
                const band = ALERT_LINE_HIT_BAND_LOGICAL * this.verticalPixelRatio * 1.5
                for (const seg of r.channelSegments) {
                    const dist = getDistanceFromSegment(sx, sy, seg.x1, seg.y1, seg.x2, seg.y2)
                    if (dist <= band) {
                        return { externalId: this.id, zOrder: 'top', cursorStyle: 'pointer' }
                    }
                }
            }
            if (r.trendlineSegment) {
                const seg = r.trendlineSegment
                const dist = getDistanceFromSegment(sx, sy, seg.x1, seg.y1, seg.x2, seg.y2)
                if (dist <= ALERT_LINE_HIT_BAND_LOGICAL * this.verticalPixelRatio * 1.5) {
                    return { externalId: this.id, zOrder: 'top', cursorStyle: 'pointer' }
                }
            }
        }
        for (const r of this.hitRegions) {
            const onPill =
                r.pill && sx >= r.pill.x1 && sx <= r.pill.x2 && sy >= r.pill.y1 && sy <= r.pill.y2
            const onPricePill =
                r.pricePill &&
                sx >= r.pricePill.x1 &&
                sx <= r.pricePill.x2 &&
                sy >= r.pricePill.y1 &&
                sy <= r.pricePill.y2
            const onHandle =
                r.handle &&
                Math.hypot(sx - r.handle.cx, sy - r.handle.cy) <=
                    r.handle.r + 6 * this.horizontalPixelRatio
            const onLine =
                sy >= r.lineY1 &&
                sy <= r.lineY2 &&
                sx >= 0 &&
                sx <= plotRightBmp &&
                !onPill &&
                !onPricePill
            if (r.editable && (onLine || onHandle) && !this.isOnActionChip(r, sx, sy)) {
                return { externalId: this.id, zOrder: 'top', cursorStyle: 'ns-resize' }
            }
        }
        return null
    }

    /** Show price-axis label only for alert currently active in UI (hovered/selected/dragging). */
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { palette } = this._chart.theme
        const textActive = palette.background.paper
        const textInactive = alpha(palette.text.secondary, 0.9)
        const points: LabelAxisViewConfig<number>[] = []
        for (const alert of this.getRenderableAlerts()) {
            if (!this.isAlertActive(alert) || !this.isUiExpanded(alert.id)) continue
            const price = this.getDisplayPrice(alert)
            if (price == null) continue
            const isActive = this.isAlertActive(alert)
            const textColor = isActive ? textActive : textInactive
            points.push({
                id: `chart-alert-axis-${alert.id}`,
                p1: price,
                p2: price,
                fill: false,
                fillColor: '#141722',
                labelColors: {
                    p1: '#141722',
                    p2: '#141722'
                }
            })
        }
        return points
    }

    onSettingsUpdate = (settings: Partial<ChartAlertsOnChartSettings>) => {
        if (settings.alerts) {
            this._state.alerts = settings.alerts
            const list = this._state.alerts ?? []
            if (this.temporaryAlert) {
                const tid = this.temporaryAlert.id
                if (this.isClientChartTemporaryAlertId(tid)) {
                    const row = list.find((a) => a.id === tid)
                    if (row) {
                        this.temporaryAlertSyncedIntoChartSettings = true
                        if (
                            row.status === UserAlert_Status.STOPPED ||
                            row.status === UserAlert_Status.DELETED ||
                            row.status === UserAlert_Status.INVALID
                        ) {
                            this.clearTemporaryAlertInternal()
                        } else if (row.status === UserAlert_Status.ALERT_STATUS_UNKNOWN) {
                            this.temporaryAlert = {
                                ...this.temporaryAlert,
                                status: UserAlert_Status.ACTIVE
                            }
                        }
                    } else if (this.temporaryAlertSyncedIntoChartSettings) {
                        this.clearTemporaryAlertInternal()
                    }
                }
            }
            const ids = new Set(list.map((a) => a.id))
            if (this.temporaryAlert?.id) ids.add(this.temporaryAlert.id)
            for (const id of Object.keys(this.pendingOverlayPriceByAlertId)) {
                if (!ids.has(id)) {
                    delete this.pendingOverlayPriceByAlertId[id]
                    continue
                }
                const alert = this.getAlertById(id)
                if (!alert) continue
                const server = getPrimaryChartAlertThresholdPrice(alert)
                const pend = this.pendingOverlayPriceByAlertId[id]
                if (server !== undefined && pend != null && Math.abs(server - pend) < 1e-8) {
                    delete this.pendingOverlayPriceByAlertId[id]
                }
            }
            void this.refreshExpandedPillTexts()
        }
        this._settings = {
            ...this._settings,
            ...settings
        }
        this.primitive?.requestUpdate()
    }

    getAvailableSettings = (): DrawingSettingGroup<ChartAlertsOnChartSettings, SettingValues>[] => []

    getDrawingType(): DrawingTypes {
        return 'chartAlertsOnChart'
    }

    getSettingsValues(): ChartAlertsOnChartSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => [] as (keyof ChartAlertsOnChartSettings)[]

    destroy(publish?: boolean, pushToHistory?: boolean, chartTypeToHistory?: boolean): void {
        this.detachDocDragListeners()
        this.setDragScrollLock(false)
        this.removeOverlayInlinePriceEditor()
        if (this._chartApi) {
            this._chartApi.unsubscribeClick(this.handleChartClick)
            this._chartApi.unsubscribeDblClick(this.handleChartDoubleClick)
        }
        const paneElement = this._chart.getPaneElementById(this._paneId)
        if (paneElement) {
            if (this._chart.touchEnabled) {
                paneElement.removeEventListener('touchstart', this.onPanePointerDown)
                paneElement.removeEventListener('touchend', this.onPanePointerUp)
                paneElement.removeEventListener('touchmove', this.onPaneTouchMove)
            } else {
                paneElement.removeEventListener('mousedown', this.onPanePointerDown)
                paneElement.removeEventListener('mousemove', this.onPaneMouseMove)
                paneElement.removeEventListener('mouseleave', this.onPaneMouseLeave)
            }
        }
        super.destroy(publish, pushToHistory, chartTypeToHistory)
    }
}

export default ChartAlertsOnChartTool
